import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  LogOut, Calendar, Users, Euro, TrendingUp, Phone, Mail,
  MessageCircle, Trash2, Download, Filter, CheckCircle2, Clock, XCircle, Loader2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const statusConfig = {
  pending:   { label: 'Pending',   color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-800 border-blue-300',      icon: CheckCircle2 },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800 border-green-300',   icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800 border-red-300',         icon: XCircle },
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, logout, authedAxios } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const [bRes, aRes] = await Promise.all([
        authedAxios.get('/admin/bookings'),
        authedAxios.get('/admin/analytics'),
      ]);
      setBookings(bRes.data);
      setAnalytics(aRes.data);
    } catch (err) {
      toast({ title: 'Failed to load', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, []);

  const handleStatusChange = async (id, status) => {
    try {
      await authedAxios.patch(`/admin/bookings/${id}`, { status });
      toast({ title: 'Updated', description: `Booking marked as ${status}` });
      loadData();
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this booking permanently?')) return;
    try {
      await authedAxios.delete(`/admin/bookings/${id}`);
      toast({ title: 'Deleted', description: 'Booking removed' });
      loadData();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const exportCsv = () => {
    const headers = ['ID', 'Date', 'Customer', 'Email', 'Phone', 'Pickup', 'Drop-off', 'Passengers', 'Car', 'Price', 'Payment', 'Status', 'Created'];
    const rows = bookings.map(b => [
      b.id, b.date, b.name, b.email, b.phone, b.pickup_location, b.dropoff_location,
      b.passengers, b.car_type, b.price || '', b.payment_method, b.status, b.created_at,
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${(c ?? '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredBookings = filter === 'all' ? bookings : bookings.filter(b => b.status === filter);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-xs sm:text-sm text-gray-500">Golden Corfu Transfer • {user?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/')} data-testid="back-to-site">
              View Site
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="admin-logout">
              <LogOut size={16} className="mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-8 py-6 space-y-6">
        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Total Bookings</span>
                <Calendar className="text-blue-500" size={18} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{analytics.total_bookings}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Pending</span>
                <Clock className="text-yellow-500" size={18} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{analytics.by_status.pending}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Completed</span>
                <CheckCircle2 className="text-green-500" size={18} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{analytics.by_status.completed}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Revenue</span>
                <Euro className="text-emerald-500" size={18} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{analytics.estimated_revenue}€</p>
            </CardContent></Card>
          </div>
        )}

        {/* Top Destinations */}
        {analytics && analytics.top_destinations.length > 0 && (
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="text-blue-600" size={20} />
                <h2 className="font-bold text-gray-900">Top Destinations</h2>
              </div>
              <div className="space-y-2">
                {analytics.top_destinations.map((d, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-700">{d.destination}</span>
                    <span className="text-sm font-bold text-blue-600">{d.count} booking{d.count > 1 ? 's' : ''}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bookings Table */}
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Filter size={18} className="text-gray-500" />
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  data-testid="status-filter"
                  className="border rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="all">All ({bookings.length})</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <Button onClick={exportCsv} size="sm" variant="outline" data-testid="export-csv">
                <Download size={16} className="mr-1" /> Export CSV
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-12 text-gray-500">
                <Loader2 className="animate-spin mx-auto mb-2" size={28} />
                Loading bookings…
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Calendar className="mx-auto mb-2 text-gray-300" size={40} />
                No bookings yet.
              </div>
            ) : (
              <div className="space-y-3">
                {filteredBookings.map((b) => {
                  const StatusIcon = statusConfig[b.status].icon;
                  const waNum = (b.phone || '').replace(/[^0-9]/g, '');
                  return (
                    <div key={b.id} data-testid={`booking-${b.id}`} className="border rounded-2xl p-4 hover:shadow-md transition">
                      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h3 className="font-bold text-gray-900">{b.name}</h3>
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 border rounded-full ${statusConfig[b.status].color}`}>
                              <StatusIcon size={12} /> {statusConfig[b.status].label}
                            </span>
                            {b.price && (
                              <span className="text-sm font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-full">{b.price}</span>
                            )}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                            <div>📍 <strong>From:</strong> {b.pickup_location}</div>
                            <div>🎯 <strong>To:</strong> {b.dropoff_location}</div>
                            <div>📅 <strong>Date:</strong> {b.date}</div>
                            <div>👥 <strong>{b.passengers} pax</strong> • {b.car_type === 'big' ? 'Big Car' : 'Small Car'}</div>
                            <div>💳 <strong>Payment:</strong> {b.payment_method === 'cash' ? 'Cash' : 'Card'}</div>
                            <div className="text-xs text-gray-400">Booked: {new Date(b.created_at).toLocaleString()}</div>
                          </div>
                          {b.message && (
                            <div className="mt-2 p-2 bg-blue-50 text-blue-800 text-xs rounded-lg">📝 {b.message}</div>
                          )}
                          <div className="flex flex-wrap gap-2 mt-3">
                            <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100">
                              <Phone size={12} /> {b.phone}
                            </a>
                            <a href={`mailto:${b.email}`} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full hover:bg-purple-100">
                              <Mail size={12} /> {b.email}
                            </a>
                            <a href={`https://wa.me/${waNum}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-green-50 text-green-700 rounded-full hover:bg-green-100">
                              <MessageCircle size={12} /> WhatsApp
                            </a>
                          </div>
                        </div>
                        <div className="flex lg:flex-col gap-2">
                          <select
                            value={b.status}
                            onChange={(e) => handleStatusChange(b.id, e.target.value)}
                            data-testid={`status-${b.id}`}
                            className="border rounded-lg px-2 py-1.5 text-xs bg-white"
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                          <Button size="sm" variant="outline" onClick={() => handleDelete(b.id)} data-testid={`delete-${b.id}`} className="text-red-600 hover:bg-red-50 border-red-200">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminDashboard;
